import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { captureError } from '../lib/errorTracker';
import { assertValidTransaction, validateClient, validateSupplier, validateBroker, validateBranch } from '../lib/validators';
import { computeSaleChargeBreakdown } from '../lib/orderCharges';
// Removed mock data dependency; initial state is empty and populated from DB when useDb=true

const DataContext = createContext(null);

export const useData = () => useContext(DataContext);

const LOCAL_PREFERENCES_KEY = 'proyecto-efe:preferences';
const LOCAL_TX_KEY = 'proyecto-efe:transactions:v1';

const cleanPayload = (payload = {}) => Object.fromEntries(
  Object.entries(payload).filter(([, value]) => value !== undefined)
);

const normalizeList = (items, normalizer) => (Array.isArray(items) ? items.map((item) => normalizer(item)) : []);

const generateCode = (name = '', prefixFallback = 'ENT') => {
  const prefix = (name || prefixFallback)
    .toString()
    .trim()
    .replace(/[^a-zA-Z0-9]/g, '')
    .slice(0, 4)
    .toUpperCase() || prefixFallback;
  const suffix = Date.now().toString(36).slice(-4).toUpperCase();
  return `${prefix}-${suffix}`;
};

const generateSaleChargeId = () => `salecharge-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

// Transaction status state machine (frontend guard)
const STATUS_ALLOWED = {
  sale: {
    pending_deposit: ['pending_delivery', 'cancelled'],
    pending_delivery: ['delivered', 'cancelled'],
    delivered: [],
    cancelled: [],
  },
  order: {
    pending_deposit: ['delivered', 'cancelled'],
    delivered: [],
    cancelled: [],
  },
  transfer: {
    delivered: [],
    cancelled: [],
  },
};

const canTransitionStatus = (type, fromStatus, toStatus) => {
  if (!type) return false;
  if (fromStatus === toStatus) return true;
  if (toStatus === 'cancelled') return true;
  const map = STATUS_ALLOWED[type] || {};
  const allowedNext = map[fromStatus] || [];
  return allowedNext.includes(toStatus);
};

const ensureSaleChargeLabel = (kind, label) => {
  if (label && String(label).trim() !== '') return String(label).trim();
  return kind === 'external' ? 'Comisión externa' : 'Comisión interna';
};

const normalizeSaleChargeEntry = (entry) => {
  if (!entry || typeof entry !== 'object') return null;
  const kind = entry.kind === 'external' ? 'external' : 'internal';
  const type = entry.type === 'flat' ? 'flat' : 'percent';
  const mode = entry.mode === 'inclusive' ? 'inclusive' : 'on_top';
  const rawValue = entry.value ?? entry.amount ?? entry.percent ?? 0;
  const value = Number(rawValue);
  if (!Number.isFinite(value)) return null;
  return {
    id: entry.id || entry.chargeId || generateSaleChargeId(),
    kind,
    type,
    mode,
    value,
    label: ensureSaleChargeLabel(kind, entry.label),
  };
};

const normalizeSaleChargeArray = (value) => {
  if (!value) return [];
  let arrayCandidate = value;
  if (typeof arrayCandidate === 'string') {
    try {
      arrayCandidate = JSON.parse(arrayCandidate);
    } catch {
      arrayCandidate = [];
    }
  }
  if (!Array.isArray(arrayCandidate)) return [];
  return arrayCandidate
    .map(normalizeSaleChargeEntry)
    .filter((charge) => charge && Number.isFinite(charge.value) && charge.value !== 0);
};

const serializeSaleChargesForStorage = (charges) => {
  const normalized = normalizeSaleChargeArray(charges);
  if (!Array.isArray(normalized) || normalized.length === 0) return undefined;
  return normalized.map((charge) => ({
    ...charge,
    value: Number(charge.value),
  }));
};

const normalizeSupplier = (supplier) => {
  if (!supplier) return supplier;
  const normalized = { ...supplier };
  if (normalized.contact === undefined) normalized.contact = normalized.contact_name ?? '';
  if (normalized.accountName === undefined) normalized.accountName = normalized.account_name ?? '';
  // Harmonize pickupAddress and address
  if (normalized.pickupAddress === undefined && normalized.address !== undefined) {
    normalized.pickupAddress = normalized.address;
  }
  if (normalized.address === undefined && normalized.pickupAddress !== undefined) {
    normalized.address = normalized.pickupAddress;
  }
  if (normalized.purchasePrice === undefined && normalized.purchase_price !== undefined) {
    normalized.purchasePrice = normalized.purchase_price;
  }
  // New: normalize provider fee percentage (fee_rate -> feeRate)
  if (normalized.feeRate === undefined && normalized.fee_rate !== undefined) {
    const fr = Number(normalized.fee_rate);
    normalized.feeRate = Number.isNaN(fr) ? undefined : fr;
  }
  // New defaults for order costing
  if (normalized.defaultCostAmount === undefined && normalized.default_cost_amount !== undefined) {
    const v = Number(normalized.default_cost_amount);
    normalized.defaultCostAmount = Number.isNaN(v) ? undefined : v;
  }
  if (normalized.defaultPurchasePercent === undefined && normalized.default_purchase_percent !== undefined) {
    const v = Number(normalized.default_purchase_percent);
    normalized.defaultPurchasePercent = Number.isNaN(v) ? undefined : v;
  }
  if (normalized.balance === undefined && normalized.current_balance !== undefined) {
    const parsedBalance = Number(normalized.current_balance);
    normalized.balance = Number.isNaN(parsedBalance) ? 0 : parsedBalance;
  }
  if (normalized.balance === undefined) normalized.balance = 0;
  // default order charges as array
  if (normalized.defaultOrderCharges === undefined && normalized.default_order_charges !== undefined) {
    try {
      normalized.defaultOrderCharges = Array.isArray(normalized.default_order_charges)
        ? normalized.default_order_charges
        : JSON.parse(normalized.default_order_charges || '[]');
    } catch {
      normalized.defaultOrderCharges = [];
    }
  }
  return normalized;
};

const normalizeClient = (client) => {
  if (!client) return client;
  const n = { ...client };
  if (n.name === undefined && n.company_name !== undefined) n.name = n.company_name;
  if (n.contact === undefined && n.contact_name !== undefined) n.contact = n.contact_name;
  if (n.deliveryAddress === undefined && n.address !== undefined) n.deliveryAddress = n.address;
  if (n.balance === undefined && n.current_balance !== undefined) n.balance = Number(n.current_balance) || 0;
  let saleCharges = normalizeSaleChargeArray(n.defaultSaleCharges);
  if ((!saleCharges || saleCharges.length === 0) && n.default_sale_charges !== undefined) {
    saleCharges = normalizeSaleChargeArray(n.default_sale_charges);
  }
  // Fallback to metadata if column doesn't exist
  if ((!saleCharges || saleCharges.length === 0) && n.metadata?.defaultSaleCharges !== undefined) {
    saleCharges = normalizeSaleChargeArray(n.metadata.defaultSaleCharges);
  }
  if (!saleCharges || saleCharges.length === 0) {
    const legacy = Number(n.defaultSalePercentage ?? n.default_sale_percentage ?? 0);
    if (Number.isFinite(legacy) && legacy !== 0) {
      saleCharges = normalizeSaleChargeArray([
        {
          kind: 'internal',
          type: 'percent',
          mode: 'on_top',
          value: legacy,
          label: 'Comisión interna',
        },
      ]);
    }
  }
  n.defaultSaleCharges = Array.isArray(saleCharges) ? saleCharges : [];
  delete n.default_sale_charges;
  const primaryPercentCharge = n.defaultSaleCharges.find((charge) => charge.type === 'percent' && charge.kind === 'internal')
    || n.defaultSaleCharges.find((charge) => charge.type === 'percent');
  if (primaryPercentCharge) {
    n.defaultSalePercentage = Number(primaryPercentCharge.value) || '';
  } else if (n.defaultSalePercentage !== undefined) {
    const candidate = Number(n.defaultSalePercentage);
    n.defaultSalePercentage = Number.isFinite(candidate) && candidate !== 0 ? candidate : '';
  } else {
    n.defaultSalePercentage = '';
  }
  return n;
};

const normalizeBroker = (broker) => {
  if (!broker) return broker;
  const normalized = { ...broker };
  if (normalized.commissionRate === undefined && normalized.commission_rate !== undefined) {
    normalized.commissionRate = Number(normalized.commission_rate);
  }
  if (normalized.phone === undefined && normalized.phone_number !== undefined) {
    normalized.phone = normalized.phone_number;
  }
  if (normalized.balance === undefined) normalized.balance = 0;
  return normalized;
};

const normalizeBranch = (branch) => {
  if (!branch) return branch;
  const normalized = { ...branch };
  if (normalized.initialCash === undefined && normalized.initial_cash !== undefined) {
    const ic = Number(normalized.initial_cash);
    normalized.initialCash = Number.isNaN(ic) ? 0 : ic;
  }
  if (normalized.cashBalance === undefined && normalized.cash_balance !== undefined) {
    normalized.cashBalance = Number(normalized.cash_balance);
  }
  // If initialCash exists but cashBalance not set, prefer initialCash for display-only legacy UIs
  if (normalized.cashBalance === undefined && normalized.initialCash !== undefined) {
    normalized.cashBalance = Number(normalized.initialCash) || 0;
  }
  if (normalized.cashBalance === undefined) normalized.cashBalance = 0;
  if (normalized.managerId === undefined && normalized.manager_id !== undefined) {
    normalized.managerId = normalized.manager_id;
  }
  return normalized;
};

const normalizeProfitCenterSummary = (center) => {
  if (!center) return center;
  const n = { ...center };
  if (n.total_revenue !== undefined) {
    const val = Number(n.total_revenue);
    n.total_revenue = Number.isNaN(val) ? 0 : val;
  }
  if (n.total_costs !== undefined) {
    const val = Number(n.total_costs);
    n.total_costs = Number.isNaN(val) ? 0 : val;
  }
  if (n.net_profit !== undefined) {
    const val = Number(n.net_profit);
    n.net_profit = Number.isNaN(val) ? 0 : val;
  }
  if (n.current_balance !== undefined) {
    const val = Number(n.current_balance);
    n.current_balance = Number.isNaN(val) ? 0 : val;
  }
  return n;
};

const defaultPreferences = {
  position: 'top-right',
  maxItems: 4,
  durationMs: 4000,
  theme: 'dark',
  compact: false,
  accentColor: '#3B82F6',
  defaultPage: '/',
  showTips: true,
};

export function DataProvider({ children, useDb = false }) {
  const [loading, setLoading] = useState(useDb);
  const [error, setError] = useState(null);
  // Coalesce concurrent refreshes to avoid duplicate network calls
  const refreshInFlightRef = React.useRef(new Map()); // key -> { promise, startedAt }

  const [currentUser, setCurrentUser] = useState(null);
  const [hasSession, setHasSession] = useState(useDb ? false : true);
  const [sessionInitialized, setSessionInitialized] = useState(false);
  const [branches, setBranches] = useState([]);
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [transactions, setTransactionsState] = useState([]);
  const [accountBalances, setAccountBalances] = useState([]);
  const [clientBalances, setClientBalances] = useState([]);
  const [supplierBalances, setSupplierBalances] = useState([]);
  const [brokerBalances, setBrokerBalances] = useState([]);
  const [branchBalances, setBranchBalances] = useState([]);
  const [branchMovements, setBranchMovements] = useState([]);
  const [transactionProfits, setTransactionProfits] = useState([]);
  const [transactionProfitsRaw, setTransactionProfitsRaw] = useState([]);
  const [branchProfits, setBranchProfits] = useState([]);
  const [profitCenters, setProfitCenters] = useState([]);
  const [preferences, setPreferences] = useState(defaultPreferences);
  const [schemaCapabilities, setSchemaCapabilities] = useState({
    saleChargesColumn: !useDb,
    clientDefaultSaleChargesColumn: !useDb,
    checked: !useDb,
  });

  // Load preferences from localStorage for anonymous/offline users
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = window.localStorage.getItem(LOCAL_PREFERENCES_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          setPreferences((prev) => ({ ...prev, ...parsed }));
        }
      } else {
        window.localStorage.setItem(LOCAL_PREFERENCES_KEY, JSON.stringify(defaultPreferences));
      }
    } catch (e) {
      console.warn('No se pudieron cargar las preferencias locales', e);
    }
  }, []);

  useEffect(() => {
    if (!useDb) {
      setSchemaCapabilities({ saleChargesColumn: true, clientDefaultSaleChargesColumn: true, checked: true });
      return;
    }
    let cancelled = false;
    const probeSchema = async () => {
      const results = { checked: true };

      // Check transactions.sale_charges column
      try {
        const { error: txError } = await supabase
          .from('transactions')
          .select('sale_charges')
          .limit(1);
        if (cancelled) return;
        results.saleChargesColumn = !(txError && txError.code === '42703');
      } catch (e) {
        results.saleChargesColumn = false;
      }

      // Check clients.default_sale_charges column
      try {
        const { error: clientError } = await supabase
          .from('clients')
          .select('default_sale_charges')
          .limit(1);
        if (cancelled) return;
        results.clientDefaultSaleChargesColumn = !(clientError && clientError.code === '42703');
      } catch (e) {
        results.clientDefaultSaleChargesColumn = false;
      }

      if (!cancelled) {
        setSchemaCapabilities(results);
      }
    };
    probeSchema();
    return () => {
      cancelled = true;
    };
  }, [useDb]);

  // Apply theme/compact preferences and persist snapshots
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const prefersDarkList = (typeof window !== 'undefined' && typeof window.matchMedia === 'function')
      ? window.matchMedia('(prefers-color-scheme: dark)')
      : null;
    const prefersDark = prefersDarkList ? prefersDarkList.matches : false;
    const resolvedTheme = preferences?.theme === 'system'
      ? (prefersDark ? 'dark' : 'light')
      : (preferences?.theme || 'dark');
    root.classList.toggle('dark', resolvedTheme === 'dark');
    // Apply accent color
    const accent = (preferences?.accentColor || '#3B82F6').trim();
    try {
      root.style.setProperty('--accent-color', accent);
      // derive tints for backgrounds without relying on color-mix
      const parseHex = (hex) => {
        const h = hex.replace('#','').trim();
        const full = h.length === 3 ? h.split('').map(c=>c+c).join('') : h;
        const r = parseInt(full.slice(0,2),16);
        const g = parseInt(full.slice(2,4),16);
        const b = parseInt(full.slice(4,6),16);
        if ([r,g,b].some(v => Number.isNaN(v))) return { r: 59, g: 130, b: 246 };
        return { r, g, b };
      };
      const { r, g, b } = parseHex(accent);
      // light mode subtle backgrounds
      root.style.setProperty('--accent-50-bg', `rgba(${r}, ${g}, ${b}, 0.12)`);
      root.style.setProperty('--accent-100-bg', `rgba(${r}, ${g}, ${b}, 0.18)`);
      // dark mode selection background ~30%
      root.style.setProperty('--accent-900-30-bg', `rgba(${r}, ${g}, ${b}, 0.30)`);
      // ring color
      root.style.setProperty('--accent-ring', `rgba(${r}, ${g}, ${b}, 0.45)`);
    } catch {}
    if (preferences?.compact) {
      root.classList.add('ui-compact');
    } else {
      root.classList.remove('ui-compact');
    }
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem('theme', resolvedTheme);
        window.localStorage.setItem(LOCAL_PREFERENCES_KEY, JSON.stringify(preferences));
      } catch (e) {
        console.warn('No se pudieron guardar las preferencias locales', e);
      }
    }
  }, [preferences]);

  // Initialize session on mount
  useEffect(() => {
    if (!useDb) return;
    let mounted = true;

    const initializeSession = async () => {
      try {
        // Don't wait forever - use a reasonable timeout
        const timeoutPromise = new Promise(resolve => 
          setTimeout(() => resolve({ data: { session: null } }), 3000)
        );
        
        const sessionPromise = supabase.auth.getSession();
        const { data: { session } } = await Promise.race([sessionPromise, timeoutPromise]);
        
        if (!mounted) return;
        
        if (session?.user) {
          setHasSession(true);
          // Load profile
          try {
            const { data: profile } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', session.user.id)
              .single();
            
            if (profile && mounted) {
              setCurrentUser({ 
                id: profile.id, 
                role: profile.role, 
                branchId: profile.branch_id, 
                name: profile.full_name, 
                email: profile.email 
              });
            } else if (mounted) {
              setCurrentUser({ 
                id: session.user.id, 
                role: 'user', 
                branchId: null, 
                name: session.user.user_metadata?.full_name || session.user.email, 
                email: session.user.email 
              });
            }
          } catch (e) {
            console.warn('Profile load failed', e);
          }
        } else {
          setHasSession(false);
        }
      } catch (e) {
        console.warn('Session initialization failed', e);
        if (mounted) setHasSession(false);
      } finally {
        if (mounted) {
          setSessionInitialized(true);
          setLoading(false);
        }
      }
    };

    initializeSession();
    return () => { mounted = false; };
  }, [useDb]);

  // Load core data when authenticated
  useEffect(() => {
    if (!useDb) return;
    if (!hasSession) return;
    let cancelled = false;

    const withRetry = async (fn, { tries = 3, baseMs = 300 } = {}) => {
      let attempt = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        try { return await fn(); }
        catch (e) {
          if (attempt >= tries - 1) throw e;
          await new Promise(r => setTimeout(r, baseMs * (2 ** attempt)));
          attempt += 1;
        }
      }
    };

    const loadData = async () => {
      try {
        const [
          { data: b },
          { data: c },
          { data: s },
          { data: br },
          { data: t },
          { data: vab },
          { data: vcb },
          { data: vsb },
          { data: vbb },
          { data: vbbal },
          { data: bcm },
          { data: vtpw },
          { data: vtp },
          { data: vbp },
          { data: vpcs }
        ] = await withRetry(() => Promise.all([
          supabase.from('branches').select('*').throwOnError(),
          supabase.from('clients').select('*').throwOnError(),
          supabase.from('suppliers').select('*').throwOnError(),
          supabase.from('brokers').select('*').throwOnError(),
          supabase.from('transactions').select('*, created_by_profile:profiles!transactions_created_by_fkey(email)').throwOnError(),
          supabase.from('v_account_balances').select('*').throwOnError(),
          supabase.from('v_client_balances').select('*').throwOnError(),
          supabase.from('v_supplier_balances').select('*').throwOnError(),
          supabase.from('v_broker_balances').select('*').throwOnError(),
          supabase.from('v_branch_cash_computed').select('*'),
          supabase.from('branch_cash_movements').select('*').order('created_at', { ascending: false }),
          supabase.from('v_transaction_profit_wac').select('*'),
          supabase.from('v_transaction_profit_summary').select('*'),
          supabase.from('v_branch_profit_summary').select('*'),
          supabase.from('v_profit_center_summary').select('*'),
        ]));

        if (cancelled) return;
        setBranches(normalizeList(b, normalizeBranch));
        setClients(normalizeList(c, normalizeClient));
        setSuppliers(normalizeList(s, normalizeSupplier));
        setBrokers(normalizeList(br, normalizeBroker));
        setTransactions(t || []);
        setAccountBalances(vab || []);
        setClientBalances(vcb || []);
        setSupplierBalances(vsb || []);
        setBrokerBalances(vbb || []);
        setBranchBalances(vbbal || []);
        setBranchMovements(Array.isArray(bcm) ? bcm : []);
        setTransactionProfits((Array.isArray(vtpw) && vtpw.length >= 0) ? (vtpw || []) : (vtp || []));
        setTransactionProfitsRaw(vtp || []);
        setBranchProfits(vbp || []);
        setProfitCenters(normalizeList(vpcs, normalizeProfitCenterSummary));
      } catch (e) {
        if (e && typeof e === 'object') {
          console.error('DataProvider data load error', {
            message: e.message,
            name: e.name,
            status: e.status,
            code: e.code,
            details: e.details,
            hint: e.hint,
          });
          try { captureError(e, { stage: 'DataProvider.dataLoad' }); } catch {}
        } else {
          console.error('DataProvider data load error', e);
          try { captureError(new Error(String(e)), { stage: 'DataProvider.dataLoad' }); } catch {}
        }
        if (!cancelled) setError(e);
      }
    };

    loadData();
    return () => { cancelled = true; };
  }, [useDb, hasSession]);

  // Keep currentUser in sync with auth state (so logout hides the app and shows /login)
  useEffect(() => {
    if (!useDb) return undefined;
    const { data: subscription } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Handle sign out
      if (event === 'SIGNED_OUT') {
        setCurrentUser(null);
        setHasSession(false);
      }
      
      // Handle sign in (after initial load)
      if (event === 'SIGNED_IN' && session?.user && sessionInitialized) {
        setHasSession(true);
        try {
          const user = session.user;
          const { data: profile } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', user.id)
            .single();
          if (profile) {
            setCurrentUser({ id: profile.id, role: profile.role, branchId: profile.branch_id, name: profile.full_name, email: profile.email });
          } else {
            const fallback = { id: user.id, role: 'user', branchId: null, name: user.user_metadata?.full_name || user.email, email: user.email };
            setCurrentUser(fallback);
            try {
              await supabase.from('profiles').upsert({ id: user.id, email: user.email, full_name: fallback.name, role: 'user', is_active: true }).select('id').single();
            } catch {}
          }
        } catch {}
      }
    });
    return () => {
      subscription?.subscription?.unsubscribe?.();
    };
  }, [useDb, sessionInitialized]);

  // In mock mode, hydrate transactions from localStorage (to persist status across refresh)
  useEffect(() => {
    if (useDb) return;
    if (typeof window === 'undefined') return;
    try {
      const raw = window.localStorage.getItem(LOCAL_TX_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          setTransactionsState(parsed);
        }
      }
    } catch (e) {
      console.warn('No se pudieron cargar transacciones locales', e);
    }
  }, [useDb]);

  // Persist transactions to localStorage in mock mode whenever they change
  useEffect(() => {
    if (useDb) return;
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(LOCAL_TX_KEY, JSON.stringify(transactions));
    } catch (e) {
      console.warn('No se pudieron guardar transacciones locales', e);
    }
  }, [useDb, transactions]);

  const getSessionUserId = async () => {
    if (!useDb) return null;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      return session?.user?.id || null;
    } catch (e) {
      console.warn('No se pudo obtener la sesión actual', e);
      return null;
    }
  };

  // Selective refresh helper to avoid over-fetching; also coalesces concurrent calls
  const refreshSelective = async (keys = []) => {
    if (!useDb) return;
    const setKey = Array.isArray(keys) && keys.length ? [...new Set(keys)].sort().join(',') : 'default:all-core';
    const existing = refreshInFlightRef.current.get(setKey);
    if (existing && existing.promise) {
      return existing.promise;
    }
    const fetchers = {
      transactions: () => supabase.from('transactions').select('*, created_by_profile:profiles!transactions_created_by_fkey(email)').throwOnError(),
      v_account_balances: () => supabase.from('v_account_balances').select('*').throwOnError(),
      v_client_balances: () => supabase.from('v_client_balances').select('*').throwOnError(),
      v_supplier_balances: () => supabase.from('v_supplier_balances').select('*').throwOnError(),
      v_broker_balances: () => supabase.from('v_broker_balances').select('*').throwOnError(),
      v_branch_balances: () => supabase.from('v_branch_cash_computed').select('*'),
      branch_cash_movements: () => supabase.from('branch_cash_movements').select('*').order('created_at', { ascending: false }),
      v_transaction_profit_wac: () => supabase.from('v_transaction_profit_wac').select('*'),
      v_transaction_profit_summary: () => supabase.from('v_transaction_profit_summary').select('*'),
      v_branch_profit_summary: () => supabase.from('v_branch_profit_summary').select('*'),
      v_profit_center_summary: () => supabase.from('v_profit_center_summary').select('*'),
    };
    const plan = (Array.isArray(keys) && keys.length)
      ? keys
      : [
          'transactions',
          'v_account_balances',
          'v_client_balances',
          'v_supplier_balances',
          'v_broker_balances',
          'v_branch_balances',
          'branch_cash_movements',
          'v_transaction_profit_wac',
          'v_transaction_profit_summary',
          'v_branch_profit_summary',
          'v_profit_center_summary',
        ];

    const exec = (async () => {
      const results = await Promise.all(plan.map((k) => fetchers[k]()))
        .catch((e) => { refreshInFlightRef.current.delete(setKey); throw e; });
      const pick = (res) => (res && res.data) ? res.data : undefined;
      let idx = 0;
      for (const k of plan) {
        const res = results[idx++];
        switch (k) {
          case 'transactions': setTransactions(pick(res) || []); break;
          case 'v_account_balances': setAccountBalances(pick(res) || []); break;
          case 'v_client_balances': setClientBalances(pick(res) || []); break;
          case 'v_supplier_balances': setSupplierBalances(pick(res) || []); break;
          case 'v_broker_balances': setBrokerBalances(pick(res) || []); break;
          case 'v_branch_balances': setBranchBalances(pick(res) || []); break;
          case 'branch_cash_movements': setBranchMovements(Array.isArray(pick(res)) ? pick(res) : []); break;
          case 'v_transaction_profit_wac': {
            const wac = pick(res);
            if (Array.isArray(wac) && wac.length >= 0) setTransactionProfits(wac || []);
            break;
          }
          case 'v_transaction_profit_summary': setTransactionProfitsRaw(pick(res) || []); break;
          case 'v_branch_profit_summary': setBranchProfits(pick(res) || []); break;
          case 'v_profit_center_summary': setProfitCenters(normalizeList(pick(res), normalizeProfitCenterSummary)); break;
          default: break;
        }
      }
      return true;
    })();
    refreshInFlightRef.current.set(setKey, { promise: exec, startedAt: Date.now() });
    try {
      return await exec;
    } finally {
      setTimeout(() => refreshInFlightRef.current.delete(setKey), 150);
    }
  };

  // Backwards-compatible full refresh wrapper
  const refresh = async () => refreshSelective();

  const applyTransactionUpdate = (id, updater) => {
    setTransactions(prev => prev.map(t => (String(t.id) === String(id) ? updater(t) : t)));
  };

  const mapTransactionToPayload = (tx) => {
    const paidFlag = tx.brokerCommissionPaid ?? tx.broker_commission_paid;
    const type = tx.type || tx.transaction_type;
    // Normalize status to DB-supported values; map legacy 'pending' -> 'pending_deposit'
    const normalizedStatus = (() => {
      const s = tx.status || 'pending_deposit';
      if (s === 'pending') return 'pending_deposit';
      // known statuses
      const ok = new Set(['pending_deposit', 'pending_delivery', 'delivered', 'cancelled']);
      return ok.has(s) ? s : 'pending_deposit';
    })();
    const metadata = { ...(tx.metadata && typeof tx.metadata === 'object' ? tx.metadata : {}) };
    const initialBranchId = tx.branchId ?? tx.branch_id ?? null;
    let branchIdForPayload = initialBranchId;
    const supportsSaleChargesColumn = Boolean(schemaCapabilities.saleChargesColumn);
    const normalizedSaleCharges = serializeSaleChargesForStorage(tx.saleCharges);

    if (!supportsSaleChargesColumn && Array.isArray(normalizedSaleCharges) && normalizedSaleCharges.length > 0) {
      metadata.saleCharges = normalizedSaleCharges;
    }

    if (type === 'transfer') {
      const sourceBranch = tx.sourceBranchId ?? tx.source_branch_id ?? initialBranchId;
      const targetBranch = tx.targetBranchId ?? tx.target_branch_id ?? null;
      if (branchIdForPayload == null && sourceBranch != null) {
        branchIdForPayload = sourceBranch;
      }
      const transferMeta = {
        ...(metadata.transfer && typeof metadata.transfer === 'object' ? metadata.transfer : {}),
        sourceBranchId: sourceBranch ?? null,
        targetBranchId: targetBranch ?? null,
      };
      metadata.transfer = transferMeta;
      if (sourceBranch !== undefined) metadata.sourceBranchId = sourceBranch ?? null;
      if (targetBranch !== undefined) metadata.targetBranchId = targetBranch ?? null;
    } else {
      if (metadata.transfer) delete metadata.transfer;
      if (metadata.sourceBranchId !== undefined) delete metadata.sourceBranchId;
      if (metadata.targetBranchId !== undefined) delete metadata.targetBranchId;
    }

    const payload = {
      transaction_type: type,
      status: normalizedStatus,
      branch_id: branchIdForPayload,
      client_id: tx.clientId || null,
      supplier_id: tx.supplierId || null,
      broker_id: tx.brokerId || null,
      amount: Number(tx.amount || 0),
      broker_commission: tx.brokerCommission != null ? Number(tx.brokerCommission) : null,
      description: tx.description || null,
      deposit_amount: tx.depositAmount != null ? Number(tx.depositAmount) : null,
      delivered_amount: tx.deliveredAmount != null ? Number(tx.deliveredAmount) : null,
      // Order costing fields (nullable for sales)
      cost_amount: tx.costAmount != null ? Number(tx.costAmount) : null,
      purchase_percent: tx.purchasePercent != null ? Number(tx.purchasePercent) : null,
  // Only include sale_charges/order_charges if the feature is in use, to avoid schema errors on older DBs
  order_charges: Array.isArray(tx.orderCharges) && tx.orderCharges.length > 0 ? tx.orderCharges : undefined,
      // Pass-through metadata when present (jsonb column exists in schema)
      metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
    };
    if (supportsSaleChargesColumn && Array.isArray(normalizedSaleCharges) && normalizedSaleCharges.length > 0) {
      payload.sale_charges = normalizedSaleCharges;
    }
    if (paidFlag != null) {
      payload.broker_commission_paid = Boolean(paidFlag);
    }
    return payload;
  };

  const supplierToPayload = (input, { ensureCode = true } = {}) => {
    const supplier = normalizeSupplier(input);
    if (!supplier?.name) {
      throw new Error('El proveedor requiere un nombre.');
    }
    const code = supplier.code || (ensureCode ? generateCode(supplier.name, 'SUP') : undefined);
    const feeRate = supplier.feeRate != null ? Number(supplier.feeRate) : null;
    const defaultCost = supplier.defaultCostAmount != null ? Number(supplier.defaultCostAmount) : null;
    const defaultPct = supplier.defaultPurchasePercent != null ? Number(supplier.defaultPurchasePercent) : null;
    return cleanPayload({
      name: supplier.name,
      contact_name: supplier.contact ?? null,
      phone: supplier.phone ?? null,
      email: supplier.email ?? null,
      // Persist address; prefer explicit address, else fall back to pickupAddress from UI
      address: (supplier.address ?? supplier.pickupAddress) ?? null,
      fee_rate: feeRate != null && !Number.isNaN(feeRate) ? feeRate : null,
      default_cost_amount: defaultCost != null && !Number.isNaN(defaultCost) ? defaultCost : null,
      default_purchase_percent: defaultPct != null && !Number.isNaN(defaultPct) ? defaultPct : null,
      // Only include default_order_charges when non-empty
      default_order_charges: Array.isArray(supplier.defaultOrderCharges) && supplier.defaultOrderCharges.length > 0
        ? supplier.defaultOrderCharges
        : undefined,
      code,
      is_active: supplier.isActive ?? true,
    });
  };

  const clientToPayload = (input, { ensureCode = true } = {}) => {
    const c = normalizeClient(input);
    if (!c?.name) throw new Error('El cliente requiere un nombre.');
    const code = c.code || (ensureCode ? generateCode(c.name, 'CLI') : undefined);
    const supportsDefaultSaleChargesColumn = Boolean(schemaCapabilities.clientDefaultSaleChargesColumn);
    const normalizedSaleCharges = serializeSaleChargesForStorage(c.defaultSaleCharges);

    const payload = {
      company_name: c.name,
      contact_name: c.contact ?? null,
      phone: c.phone ?? null,
      email: c.email ?? null,
      address: c.deliveryAddress ?? c.address ?? null,
      branch_id: c.branchId ?? null,
      code,
      is_active: c.isActive ?? true,
    };

    // Store charges in column if it exists, otherwise use metadata
    if (supportsDefaultSaleChargesColumn) {
      payload.default_sale_charges = normalizedSaleCharges;
    } else if (Array.isArray(normalizedSaleCharges) && normalizedSaleCharges.length > 0) {
      const existingMetadata = c.metadata && typeof c.metadata === 'object' ? c.metadata : {};
      payload.metadata = { ...existingMetadata, defaultSaleCharges: normalizedSaleCharges };
    }

    return cleanPayload(payload);
  };

  const brokerToPayload = (input, { ensureCode = false } = {}) => {
    const broker = normalizeBroker(input);
    if (!broker?.name) {
      throw new Error('El broker requiere un nombre.');
    }
    const commissionRate = broker.commissionRate != null ? Number(broker.commissionRate) : null;
    const code = broker.code || (ensureCode ? generateCode(broker.name, 'BRK') : undefined);
    return cleanPayload({
      name: broker.name,
      phone: broker.phone ?? null,
      email: broker.email ?? null,
      commission_rate: commissionRate != null && !Number.isNaN(commissionRate) ? commissionRate : 0,
      code,
      is_active: broker.isActive ?? true,
    });
  };

  const branchToPayload = (input, { ensureCode = true } = {}) => {
    const branch = normalizeBranch(input);
    if (!branch?.name) {
      throw new Error('La sucursal requiere un nombre.');
    }
    const code = branch.code || (ensureCode ? generateCode(branch.name, 'BR') : undefined);
    return cleanPayload({
      name: branch.name,
      code,
      address: branch.address ?? null,
      phone: branch.phone ?? null,
      email: branch.email ?? null,
      manager_id: branch.managerId ?? null,
      is_active: branch.isActive ?? true,
      // Persist an initial baseline for cash in branches; balances view adds movements on top
      initial_cash: branch.initialCash != null ? Number(branch.initialCash) : (branch.cashBalance != null ? Number(branch.cashBalance) : undefined),
    });
  };

  // Normalize transactions to autofill fields when possible
  const normalizeTransactions = (txs, { clients, suppliers, brokers }) => {
    if (!Array.isArray(txs)) return [];
    return txs.map((t) => {
      let changed = false;
      const next = { ...t };

      // createdByEmail from joined profile
      if (next.createdByEmail === undefined) {
        const maybeEmail = next?.created_by_profile?.email;
        if (maybeEmail !== undefined) {
          next.createdByEmail = maybeEmail || null;
          changed = true;
        }
      }

      // Map snake_case columns when coming from DB
      if (next.type === undefined && next.transaction_type !== undefined) next.type = next.transaction_type;
      if (next.costAmount === undefined && next.cost_amount !== undefined) {
        const v = Number(next.cost_amount);
        next.costAmount = Number.isNaN(v) ? undefined : v;
      }
      if (next.purchasePercent === undefined && next.purchase_percent !== undefined) {
        const v = Number(next.purchase_percent);
        next.purchasePercent = Number.isNaN(v) ? undefined : v;
      }
      // Map created_at into a UI-friendly date when date is missing
      if (next.date === undefined && next.created_at !== undefined) {
        try {
          const d = new Date(next.created_at);
          if (!Number.isNaN(d.getTime())) {
            next.date = d.toISOString().split('T')[0];
            changed = true;
          }
        } catch {
          // ignore parse errors
        }
      }
      // Map snake_case foreign keys to camelCase ids for UI/editing
      if (next.branchId === undefined && next.branch_id !== undefined) {
        next.branchId = next.branch_id;
        changed = true;
      }
      if (next.sourceBranchId === undefined && next.source_branch_id !== undefined) {
        next.sourceBranchId = next.source_branch_id;
        changed = true;
      }
      if (next.targetBranchId === undefined && next.target_branch_id !== undefined) {
        next.targetBranchId = next.target_branch_id;
        changed = true;
      }
      if (next.targetBranchId === undefined && next.metadata && typeof next.metadata === 'object') {
        const metaTarget = next.metadata.targetBranchId ?? next.metadata?.transfer?.targetBranchId;
        if (metaTarget !== undefined) {
          next.targetBranchId = metaTarget;
          changed = true;
        }
      }
      if (next.sourceBranchId === undefined && next.metadata && typeof next.metadata === 'object') {
        const metaSource = next.metadata.sourceBranchId ?? next.metadata?.transfer?.sourceBranchId;
        if (metaSource !== undefined) {
          next.sourceBranchId = metaSource;
          changed = true;
        }
      }
      if ((next.type || next.transaction_type) === 'transfer') {
        if ((next.sourceBranchId == null || next.sourceBranchId === '') && (next.branchId != null)) {
          next.sourceBranchId = next.branchId;
          changed = true;
        }
        if ((next.branchId == null || next.branchId === '') && (next.sourceBranchId != null && next.sourceBranchId !== '')) {
          next.branchId = next.sourceBranchId;
          changed = true;
        }
      }
      if (next.clientId === undefined && next.client_id !== undefined) {
        next.clientId = next.client_id;
        changed = true;
      }
      if (next.supplierId === undefined && next.supplier_id !== undefined) {
        next.supplierId = next.supplier_id;
        changed = true;
      }
      if (next.brokerId === undefined && next.broker_id !== undefined) {
        next.brokerId = next.broker_id;
        changed = true;
      }
      if (next.orderCharges === undefined && next.order_charges !== undefined) {
        try {
          next.orderCharges = Array.isArray(next.order_charges) ? next.order_charges : JSON.parse(next.order_charges || '[]');
        } catch {
          next.orderCharges = [];
        }
      }
      if (next.saleCharges === undefined && next.sale_charges !== undefined) {
        try {
          next.saleCharges = Array.isArray(next.sale_charges) ? next.sale_charges : JSON.parse(next.sale_charges || '[]');
        } catch {
          next.saleCharges = [];
        }
        changed = true;
      }
      if ((!Array.isArray(next.saleCharges) || next.saleCharges.length === 0) && next.metadata && Array.isArray(next.metadata.saleCharges)) {
        next.saleCharges = normalizeSaleChargeArray(next.metadata.saleCharges);
        changed = true;
      }

      if (next.brokerCommission === undefined && next.broker_commission !== undefined) {
        const commissionValue = Number(next.broker_commission);
        if (!Number.isNaN(commissionValue)) {
          next.brokerCommission = commissionValue;
          changed = true;
        }
      }
      if (next.brokerCommissionPaid === undefined && next.broker_commission_paid !== undefined) {
        next.brokerCommissionPaid = Boolean(next.broker_commission_paid);
        changed = true;
      }

      const supplier = suppliers?.find(s => String(s.id) === String(t.supplierId || t.supplier_id)) || null;
      const broker = brokers?.find(b => String(b.id) === String(t.brokerId || t.broker_id)) || null;

      // depositAccount from supplier.accountName
      const hasDepositAccount = next.depositAccount != null && next.depositAccount !== '';
      if (!hasDepositAccount && supplier && supplier.accountName) {
        next.depositAccount = supplier.accountName;
        changed = true;
      }

      const isSale = (next.type || next.transaction_type) === 'sale';
      const amount = Number(next.amount || 0);

      // NOTE: We do NOT auto-apply client.defaultSaleCharges here anymore.
      // The Transactions component handles this properly with saleChargesTouched flag.
      // Auto-applying here was causing deleted charges to reappear.

      const hasSalePrice = next.salePrice != null && next.salePrice !== '';
      if (isSale && !hasSalePrice && amount > 0) {
        const charges = Array.isArray(next.saleCharges) ? next.saleCharges : [];
        if (charges.length > 0) {
          const breakdown = computeSaleChargeBreakdown({ ...next, saleCharges: charges }, { baseAmount: amount });
          if (breakdown && breakdown.totalToCollect != null) {
            next.salePrice = Math.round(Number(breakdown.totalToCollect) * 100) / 100;
            changed = true;
          }
        } else if (next.salePercentage != null) {
          const pct = Number(next.salePercentage || 0);
          if (Number.isFinite(pct)) {
            next.salePrice = Math.round((amount * (1 + pct / 100)) * 100) / 100;
            changed = true;
          }
        }
      }

      // Prefill order costing from supplier defaults
      const isOrder = (next.type || next.transaction_type) === 'order';
      if (isOrder && supplier) {
        if ((next.costAmount == null || Number.isNaN(Number(next.costAmount))) && supplier.defaultCostAmount != null) {
          next.costAmount = Number(supplier.defaultCostAmount);
          changed = true;
        }
        if ((next.purchasePercent == null || Number.isNaN(Number(next.purchasePercent))) && supplier.defaultPurchasePercent != null) {
          next.purchasePercent = Number(supplier.defaultPurchasePercent);
          changed = true;
        }
        // Prefill orderCharges from supplier defaultOrderCharges if not set
        if ((!Array.isArray(next.orderCharges) || next.orderCharges.length === 0) && Array.isArray(supplier.defaultOrderCharges) && supplier.defaultOrderCharges.length > 0) {
          next.orderCharges = supplier.defaultOrderCharges.map((c, i) => ({ id: `${Date.now()}-${i}`, ...c }));
          changed = true;
        }
      }

      // brokerCommission from broker.commissionRate
      const hasBrokerCommission = next.brokerCommission != null;
      if (!hasBrokerCommission && broker && broker.commissionRate != null) {
        const rate = Number(broker.commissionRate || 0);
        if (rate > 0 && amount > 0) {
          next.brokerCommission = Math.round((amount * rate / 100) * 100) / 100;
          changed = true;
        }
      }

      return changed ? next : t;
    });
  };

  // Wrapped setter to always normalize
  const setTransactions = (updater) => {
    setTransactionsState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      return normalizeTransactions(next, { clients, suppliers, brokers });
    });
  };

  // Re-apply normalization when related entities change
  useEffect(() => {
    setTransactionsState((prev) => normalizeTransactions(prev, { clients, suppliers, brokers }));
  }, [clients, suppliers, brokers]);

  const dbAddTransaction = async (tx) => {
    assertValidTransaction(tx);
    if (!useDb) {
      setTransactions(prev => [...prev, { ...tx, id: tx.id || Date.now() }]);
      return { data: tx };
    }
    // derive commission if not provided
    let derivedCommission = tx.brokerCommission;
    if ((derivedCommission == null || Number.isNaN(derivedCommission)) && tx.brokerId) {
      const broker = brokers.find(b => String(b.id) === String(tx.brokerId));
      const pct = tx.brokerPercentage != null ? Number(tx.brokerPercentage) : (broker?.commissionRate != null ? Number(broker.commissionRate) : null);
      const baseAmount = Number(tx.amount || 0);
      if (pct != null && baseAmount > 0) {
        derivedCommission = Math.round((baseAmount * pct / 100) * 100) / 100;
      }
    }
    const payload = {
      ...mapTransactionToPayload(tx),
      broker_commission: derivedCommission != null ? Number(derivedCommission) : null,
    };
    // Enforce server-consistent initial status for transfers
    if ((tx.type || tx.transaction_type) === 'transfer') {
      payload.status = 'delivered';
    }
    const { data, error } = await supabase
      .from('transactions')
      .insert(payload)
      .select('*, created_by_profile:profiles!transactions_created_by_fkey(email)')
      .single();
    if (error) {
      console.error('dbAddTransaction error', { message: error.message, details: error.details, hint: error.hint, code: error.code });
      throw error;
    }
    await refreshSelective([
      'transactions',
      'v_branch_balances',
      'v_account_balances',
      'v_client_balances',
      'v_supplier_balances',
      'v_broker_balances',
      'v_transaction_profit_wac',
      'v_transaction_profit_summary',
      'v_branch_profit_summary',
      'v_profit_center_summary',
    ]);
    return { data };
  };

  const dbUpdateTransaction = async (id, patch) => {
    const existing = transactions.find(t => String(t.id) === String(id));
    if (!existing) {
      throw new Error('Transacción no encontrada');
    }
    const merged = { ...existing, ...patch };
    assertValidTransaction(merged);

    // Enforce status transitions according to state machine
    const currentStatus = existing.status || 'pending_deposit';
    const nextStatus = merged.status || currentStatus;
    const txType = merged.type || merged.transaction_type;
    if ((txType === 'transfer') && nextStatus !== 'delivered') {
      throw new Error('Las transferencias deben estar en estado delivered');
    }
    if (!canTransitionStatus(txType, currentStatus, nextStatus)) {
      throw new Error(`Transición de estado inválida (${currentStatus} -> ${nextStatus})`);
    }

    if (!useDb) {
      applyTransactionUpdate(id, () => merged);
      return { data: merged };
    }

    const payload = mapTransactionToPayload(merged);
    const { data, error } = await supabase
      .from('transactions')
      .update(payload)
      .eq('id', id)
      .select('*, created_by_profile:profiles!transactions_created_by_fkey(email)')
      .single();
    if (error) {
      console.error('dbUpdateTransaction error', { message: error.message, details: error.details, hint: error.hint, code: error.code });
      throw error;
    }
    await refreshSelective([
      'transactions',
      'v_branch_balances',
      'v_account_balances',
      'v_client_balances',
      'v_supplier_balances',
      'v_broker_balances',
      'v_transaction_profit_wac',
      'v_transaction_profit_summary',
      'v_branch_profit_summary',
      'v_profit_center_summary',
    ]);
    return { data };
  };

  const dbDeleteTransaction = async (id) => {
    console.groupCollapsed('[DataProvider] dbDeleteTransaction start');
    console.info('transactionId:', id, 'useDb:', useDb);
    if (!useDb) {
      setTransactions(prev => prev.filter(t => String(t.id) !== String(id)));
      console.info('[DataProvider] local/mock delete applied');
      console.groupEnd();
      return;
    }
    // Best-effort: delete child records first to avoid FK/RLS issues
    try {
      const { error: depErr } = await supabase.from('deposits').delete().eq('transaction_id', id);
      if (depErr) {
        console.warn('[DataProvider] deposits delete warning', depErr);
      } else {
        console.info('[DataProvider] deposits cleanup ok');
      }
    } catch (e) {
      console.warn('dbDeleteTransaction deposits cleanup warning', e?.message || e);
    }
    try {
      const { error: delivErr } = await supabase.from('deliveries').delete().eq('transaction_id', id);
      if (delivErr) {
        console.warn('[DataProvider] deliveries delete warning', delivErr);
      } else {
        console.info('[DataProvider] deliveries cleanup ok');
      }
    } catch (e) {
      console.warn('dbDeleteTransaction deliveries cleanup warning', e?.message || e);
    }
    const { error } = await supabase.from('transactions').delete().eq('id', id);
    if (error) {
      console.error('dbDeleteTransaction error', { message: error.message, details: error.details, hint: error.hint, code: error.code });
      console.groupEnd();
      throw error;
    }
    console.info('[DataProvider] transaction delete ok');
    await refreshSelective([
      'transactions',
      'v_branch_balances',
      'v_account_balances',
      'v_client_balances',
      'v_supplier_balances',
      'v_broker_balances',
      'v_transaction_profit_wac',
      'v_transaction_profit_summary',
      'v_branch_profit_summary',
      'v_profit_center_summary',
    ]);
    console.groupEnd();
  };

  const dbMarkBrokerCommissionPaid = async (id, paid = true) => {
    if (!useDb) {
      applyTransactionUpdate(id, (tx) => ({ ...tx, brokerCommissionPaid: paid }));
      return;
    }
    const { error } = await supabase
      .from('transactions')
      .update({ broker_commission_paid: paid })
      .eq('id', id);
    if (error) {
      console.error('dbMarkBrokerCommissionPaid error', { message: error.message, details: error.details, hint: error.hint, code: error.code });
      throw error;
    }
    await refreshSelective([
      'transactions',
      'v_branch_balances',
      'v_transaction_profit_wac',
      'v_transaction_profit_summary',
      'v_branch_profit_summary',
      'v_profit_center_summary',
    ]);
  };

  const dbAddDeposit = async (deposit) => {
    if (!useDb) {
      // local fallback: push into transaction deposits
  setTransactions(prev => prev.map(t => t.id === deposit.transaction_id ? { ...t, deposits: [...(t.deposits||[]), deposit] } : t));
      return { data: deposit };
    }
    // best-effort: map account name to supplier_account_id
    let supplier_account_id = deposit.supplier_account_id || null;
    if (!supplier_account_id && deposit.account_name) {
      const { data: sa } = await supabase.from('supplier_accounts').select('id,account_name').eq('account_name', deposit.account_name).limit(1).maybeSingle();
      supplier_account_id = sa?.id || null;
    }
    const payload = {
      transaction_id: deposit.transaction_id,
      supplier_account_id,
      amount: Number(deposit.amount || 0),
      reference_number: deposit.reference || deposit.reference_number || null,
      bank_name: deposit.bank_name || null,
      deposit_date: deposit.deposit_date || deposit.date || null,
      // Store proof info inside metadata jsonb when provided
      metadata: deposit.metadata && typeof deposit.metadata === 'object' && Object.keys(deposit.metadata).length > 0
        ? deposit.metadata
        : undefined,
    };
    const { data, error } = await supabase.from('deposits').insert(payload).select('*').single();
    if (error) {
      console.error('dbAddDeposit error', { message: error.message, details: error.details, hint: error.hint, code: error.code });
      throw error;
    }
    await refreshSelective([
      'transactions',
      'v_branch_balances',
      'v_transaction_profit_wac',
      'v_transaction_profit_summary',
      'v_branch_profit_summary',
      'v_profit_center_summary',
    ]);
    return { data };
  };

  const dbDeleteDeposit = async (depositOrId) => {
    if (!useDb) {
      const delId = typeof depositOrId === 'object' ? depositOrId.id : depositOrId;
      setTransactions(prev => prev.map(t => ({
        ...t,
        deposits: (t.deposits || []).filter(d => d.id !== delId)
      })));
      return;
    }
    // Best-effort: delete associated proof file if metadata contains proof_path
    try {
      const meta = typeof depositOrId === 'object' ? (depositOrId.metadata || {}) : null;
      const proofPath = meta?.proof_path || meta?.proofPath || null;
      if (proofPath) {
        await supabase.storage.from('deposits').remove([proofPath]);
      }
    } catch (e) {
      console.warn('storage remove (non-fatal):', e?.message || e);
    }
    const idToDelete = typeof depositOrId === 'object' ? depositOrId.id : depositOrId;
    const { error } = await supabase.from('deposits').delete().eq('id', idToDelete);
    if (error) {
      console.error('dbDeleteDeposit error', { message: error.message, details: error.details, hint: error.hint, code: error.code });
      throw error;
    }
    await refreshSelective([
      'transactions',
      'v_branch_balances',
      'v_transaction_profit_wac',
      'v_transaction_profit_summary',
      'v_branch_profit_summary',
      'v_profit_center_summary',
    ]);
  };

  // Upload a proof file to Supabase Storage (bucket: deposits)
  const uploadDepositProof = async (file, { transactionId }) => {
    if (!useDb) {
      // In mock mode, fabricate a URL
      const fakePath = `${transactionId || 'tx'}/${Date.now()}_${file?.name || 'proof'}`;
      const fakeUrl = `mock://storage/deposits/${fakePath}`;
      return { path: fakePath, url: fakeUrl };
    }
    if (!file) return { path: null, url: null };
    const fileNameSafe = (file.name || 'proof').replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `${transactionId}/${Date.now()}_${fileNameSafe}`;
    const { error: upErr } = await supabase.storage.from('deposits').upload(path, file, {
      contentType: file.type || undefined,
      upsert: false,
    });
    if (upErr) {
      console.error('uploadDepositProof error', upErr);
      throw upErr;
    }
    const { data } = supabase.storage.from('deposits').getPublicUrl(path);
    const url = data?.publicUrl || null;
    return { path, url };
  };

  const dbListDeposits = async (transactionId) => {
    if (!useDb) {
      const tx = transactions.find(t => String(t.id) === String(transactionId));
      return { data: (tx?.deposits || []).map(d => ({ ...d })) };
    }
    const { data, error } = await supabase
      .from('deposits')
      .select('*')
      .eq('transaction_id', transactionId)
      .order('deposit_date', { ascending: true, nullsFirst: true })
      .order('id', { ascending: true });
    if (error) {
      console.error('dbListDeposits error', { message: error.message, details: error.details, hint: error.hint, code: error.code });
      throw error;
    }
    return { data };
  };

  const dbCancelTransaction = async (transactionId) => {
    if (!useDb) return;
    const { error } = await supabase
      .from('transactions')
      .update({ status: 'cancelled', cancelled_date: new Date().toISOString() })
      .eq('id', transactionId);
    if (error) {
      console.error('dbCancelTransaction error', { message: error.message, details: error.details, hint: error.hint, code: error.code });
      throw error;
    }
    await refreshSelective([
      'transactions',
      'v_branch_balances',
      'v_transaction_profit_wac',
      'v_transaction_profit_summary',
      'v_branch_profit_summary',
      'v_profit_center_summary',
    ]);
  };

  const dbDeliver = async (transactionId, deliveredAmount, proof) => {
    if (!useDb) return;
    const payload = {
      transaction_id: transactionId,
      amount: Number(deliveredAmount || 0),
    };
    const { error } = await supabase.from('deliveries').insert(payload);
    if (error) {
      console.error('dbDeliver error', { message: error.message, details: error.details, hint: error.hint, code: error.code });
      throw error;
    }
    await refreshSelective([
      'transactions',
      'v_branch_balances',
      'v_transaction_profit_wac',
      'v_transaction_profit_summary',
      'v_branch_profit_summary',
      'v_profit_center_summary',
    ]);
  };

  const dbMarkDelivered = async (transactionId) => {
    if (!useDb) return;
    const { error } = await supabase
      .from('transactions')
      .update({ status: 'delivered', delivered_date: new Date().toISOString() })
      .eq('id', transactionId);
    if (error) {
      console.error('dbMarkDelivered error', { message: error.message, details: error.details, hint: error.hint, code: error.code });
      throw error;
    }
    await refreshSelective([
      'transactions',
      'v_branch_balances',
      'v_transaction_profit_wac',
      'v_transaction_profit_summary',
      'v_branch_profit_summary',
      'v_profit_center_summary',
    ]);
  };

  const dbAddSupplier = async (supplier) => {
    const normalizedInput = normalizeSupplier(supplier);
    {
      const errs = validateSupplier(normalizedInput);
      if (errs.length) throw new Error(errs.join('. '));
    }
    if (!useDb) {
      const localSupplier = { ...normalizedInput, id: normalizedInput.id || Date.now().toString() };
      setSuppliers((prev) => [...prev, normalizeSupplier(localSupplier)]);
      return { data: localSupplier };
    }
    const userId = await getSessionUserId();
    const payload = supplierToPayload(normalizedInput, { ensureCode: true });
    if (userId) payload.created_by = userId;
    const { data, error } = await supabase
      .from('suppliers')
      .insert(payload)
      .select('*')
      .single();
    if (error) {
      console.error('dbAddSupplier error', { message: error.message, details: error.details, hint: error.hint, code: error.code });
      throw error;
    }
    const normalized = normalizeSupplier({ ...data, balance: normalizedInput.balance ?? 0 });
    setSuppliers((prev) => [...prev, normalized]);
    return { data: normalized };
  };

  const dbAddClient = async (client) => {
    console.log('[DataProvider] dbAddClient called with:', client);
    const normalizedInput = normalizeClient(client);
    console.log('[DataProvider] Normalized client:', normalizedInput);
    {
      const errs = validateClient(normalizedInput);
      console.log('[DataProvider] Validation errors:', errs);
      if (errs.length) throw new Error(errs.join('. '));
    }
    if (!useDb) {
      const localClient = { ...normalizedInput, id: normalizedInput.id || Date.now().toString() };
      setClients((prev) => [...prev, normalizeClient(localClient)]);
      return { data: localClient };
    }
    const userId = await getSessionUserId();
    const payload = clientToPayload(normalizedInput, { ensureCode: true });
    console.log('[DataProvider] Client payload for insert:', payload);
    if (userId) payload.created_by = userId;
    const { data, error } = await supabase
      .from('clients')
      .insert(payload)
      .select('*')
      .single();
    if (error) {
      console.error('dbAddClient error', { message: error.message, details: error.details, hint: error.hint, code: error.code });
      throw error;
    }
    console.log('[DataProvider] Insert successful:', data);
    const normalized = normalizeClient({ ...data, balance: normalizedInput.balance ?? 0 });
    setClients((prev) => [...prev, normalized]);
    return { data: normalized };
  };

  const dbUpdateClient = async (id, patch) => {
    const existingClient = clients.find((client) => String(client.id) === String(id)) || null;
    const normalizedPatch = normalizeClient({ ...patch, id });
    {
      const errs = validateClient({ ...(existingClient || {}), ...normalizedPatch });
      if (errs.length) throw new Error(errs.join('. '));
    }
    if (!useDb) {
      setClients((prev) => prev.map((client) => (String(client.id) === String(id) ? normalizeClient({ ...client, ...normalizedPatch }) : client)));
      return { data: normalizedPatch };
    }
    const existing = existingClient;
    const payload = clientToPayload(normalizedPatch, { ensureCode: false });
    const { data, error } = await supabase
      .from('clients')
      .update(payload)
      .eq('id', id)
      .select('*')
      .single();
    if (error) {
      console.error('dbUpdateClient error', { message: error.message, details: error.details, hint: error.hint, code: error.code });
      throw error;
    }
    const normalized = normalizeClient({ ...data, balance: normalizedPatch.balance ?? existing?.balance ?? 0 });
    setClients((prev) => prev.map((client) => (String(client.id) === String(id) ? normalized : client)));
    return { data: normalized };
  };

  const dbDeleteClient = async (id) => {
    if (!useDb) {
      setClients((prev) => prev.filter((client) => String(client.id) !== String(id)));
      return;
    }
    const { error } = await supabase.from('clients').delete().eq('id', id);
    if (error) {
      console.error('dbDeleteClient error', { message: error.message, details: error.details, hint: error.hint, code: error.code });
      throw error;
    }
    setClients((prev) => prev.filter((client) => String(client.id) !== String(id)));
  };

  const dbUpdateSupplier = async (id, patch) => {
    const existingSupplier = suppliers.find((supplier) => String(supplier.id) === String(id)) || null;
    const normalizedPatch = normalizeSupplier({ ...patch, id });
    {
      const errs = validateSupplier({ ...(existingSupplier || {}), ...normalizedPatch });
      if (errs.length) throw new Error(errs.join('. '));
    }
    if (!useDb) {
      setSuppliers((prev) => prev.map((supplier) => (String(supplier.id) === String(id) ? normalizeSupplier({ ...supplier, ...normalizedPatch }) : supplier)));
      return { data: normalizedPatch };
    }
    const existing = existingSupplier;
    const payload = supplierToPayload(normalizedPatch, { ensureCode: false });
    const { data, error } = await supabase
      .from('suppliers')
      .update(payload)
      .eq('id', id)
      .select('*')
      .single();
    if (error) {
      console.error('dbUpdateSupplier error', { message: error.message, details: error.details, hint: error.hint, code: error.code });
      throw error;
    }
    const normalized = normalizeSupplier({ ...data, balance: normalizedPatch.balance ?? existing?.balance ?? 0 });
    setSuppliers((prev) => prev.map((supplier) => (String(supplier.id) === String(id) ? normalized : supplier)));
    return { data: normalized };
  };

  const dbDeleteSupplier = async (id) => {
    if (!useDb) {
      setSuppliers((prev) => prev.filter((supplier) => String(supplier.id) !== String(id)));
      return;
    }
    const { error } = await supabase.from('suppliers').delete().eq('id', id);
    if (error) {
      console.error('dbDeleteSupplier error', { message: error.message, details: error.details, hint: error.hint, code: error.code });
      throw error;
    }
    setSuppliers((prev) => prev.filter((supplier) => String(supplier.id) !== String(id)));
  };

  const dbAddBroker = async (broker) => {
    const normalizedInput = normalizeBroker(broker);
    {
      const errs = validateBroker(normalizedInput);
      if (errs.length) throw new Error(errs.join('. '));
    }
    if (!useDb) {
      const localBroker = { ...normalizedInput, id: normalizedInput.id || Date.now().toString() };
      setBrokers((prev) => [...prev, normalizeBroker(localBroker)]);
      return { data: localBroker };
    }
    const userId = await getSessionUserId();
    const payload = brokerToPayload(normalizedInput, { ensureCode: true });
    if (userId) payload.created_by = userId;
    const { data, error } = await supabase
      .from('brokers')
      .insert(payload)
      .select('*')
      .single();
    if (error) {
      console.error('dbAddBroker error', { message: error.message, details: error.details, hint: error.hint, code: error.code });
      throw error;
    }
    const normalized = normalizeBroker({ ...data, balance: normalizedInput.balance ?? 0 });
    setBrokers((prev) => [...prev, normalized]);
    return { data: normalized };
  };

  const dbUpdateBroker = async (id, patch) => {
    const existingBroker = brokers.find((broker) => String(broker.id) === String(id)) || null;
    const normalizedPatch = normalizeBroker({ ...patch, id });
    {
      const errs = validateBroker({ ...(existingBroker || {}), ...normalizedPatch });
      if (errs.length) throw new Error(errs.join('. '));
    }
    if (!useDb) {
      setBrokers((prev) => prev.map((broker) => (String(broker.id) === String(id) ? normalizeBroker({ ...broker, ...normalizedPatch }) : broker)));
      return { data: normalizedPatch };
    }
    const existing = existingBroker;
    const payload = brokerToPayload(normalizedPatch, { ensureCode: false });
    const { data, error } = await supabase
      .from('brokers')
      .update(payload)
      .eq('id', id)
      .select('*')
      .single();
    if (error) {
      console.error('dbUpdateBroker error', { message: error.message, details: error.details, hint: error.hint, code: error.code });
      throw error;
    }
    const normalized = normalizeBroker({ ...data, balance: normalizedPatch.balance ?? existing?.balance ?? 0 });
    setBrokers((prev) => prev.map((broker) => (String(broker.id) === String(id) ? normalized : broker)));
    return { data: normalized };
  };

  const dbAddBranch = async (branch) => {
    const normalizedInput = normalizeBranch(branch);
    {
      const errs = validateBranch(normalizedInput);
      if (errs.length) throw new Error(errs.join('. '));
    }
    if (!useDb) {
      const localBranch = { ...normalizedInput, id: normalizedInput.id || Date.now().toString() };
      setBranches((prev) => [...prev, normalizeBranch(localBranch)]);
      return { data: localBranch };
    }
    const userId = await getSessionUserId();
    const payload = branchToPayload(normalizedInput, { ensureCode: true });
    if (userId) payload.manager_id = payload.manager_id || userId;
    const { data, error } = await supabase
      .from('branches')
      .insert(payload)
      .select('*')
      .single();
    if (error) {
      console.error('dbAddBranch error', { message: error.message, details: error.details, hint: error.hint, code: error.code });
      throw error;
    }
    const normalized = normalizeBranch({ ...data, cashBalance: normalizedInput.cashBalance ?? 0 });
    setBranches((prev) => [...prev, normalized]);
    return { data: normalized };
  };

  const dbUpdateBranch = async (id, patch) => {
    const existingBranch = branches.find((branchItem) => String(branchItem.id) === String(id)) || null;
    const normalizedPatch = normalizeBranch({ ...patch, id });
    {
      const errs = validateBranch({ ...(existingBranch || {}), ...normalizedPatch });
      if (errs.length) throw new Error(errs.join('. '));
    }
    if (!useDb) {
      setBranches((prev) => prev.map((branchItem) => (String(branchItem.id) === String(id) ? normalizeBranch({ ...branchItem, ...normalizedPatch }) : branchItem)));
      return { data: normalizedPatch };
    }
    const existing = existingBranch;
    const payload = branchToPayload(normalizedPatch, { ensureCode: false });
    const { data, error } = await supabase
      .from('branches')
      .update(payload)
      .eq('id', id)
      .select('*')
      .single();
    if (error) {
      console.error('dbUpdateBranch error', { message: error.message, details: error.details, hint: error.hint, code: error.code });
      throw error;
    }
    const normalized = normalizeBranch({ ...data, cashBalance: normalizedPatch.cashBalance ?? existing?.cashBalance ?? 0 });
    setBranches((prev) => prev.map((branchItem) => (String(branchItem.id) === String(id) ? normalized : branchItem)));
    return { data: normalized };
  };

  const mergePreferences = (patch = {}) => {
    let merged = null;
    setPreferences((prev) => {
      const base = prev || defaultPreferences;
      merged = { ...base, ...patch };
      return merged;
    });
    return merged || { ...defaultPreferences, ...patch };
  };

  const value = {
    loading,
    error,
    useDb,
    hasSession,
    currentUser, setCurrentUser,
    branches, setBranches,
    clients, setClients,
    suppliers, setSuppliers,
    brokers, setBrokers,
    transactions, setTransactions,
    // db helpers
    dbAddTransaction,
    dbUpdateTransaction,
    dbDeleteTransaction,
    dbAddClient,
    dbUpdateClient,
    dbDeleteClient,
  dbAddSupplier,
  dbUpdateSupplier,
  dbDeleteSupplier,
  dbAddBroker,
  dbUpdateBroker,
  dbAddBranch,
  dbUpdateBranch,
    dbAddDeposit,
  dbDeleteDeposit,
  dbListDeposits,
  uploadDepositProof,
    dbCancelTransaction,
    dbDeliver,
    dbMarkDelivered,
    dbMarkBrokerCommissionPaid,
  accountBalances, clientBalances, supplierBalances, brokerBalances, branchBalances, branchMovements,
  transactionProfits, transactionProfitsRaw, branchProfits, profitCenters,
    // Branch cash movements
    dbAddBranchCashMovement: async ({ branchId, type, amount, description }) => {
      const amt = Number(amount || 0);
      if (!useDb) {
        if (!branchId || amt <= 0) return;
        setBranches(prev => prev.map(b => {
          if (String(b.id) !== String(branchId)) return b;
          const delta = type === 'salida' ? -amt : amt;
          return { ...b, cashBalance: Number(b.cashBalance || 0) + delta };
        }));
        const mock = { id: Date.now().toString(), branch_id: branchId, movement_type: type, amount: amt, description, created_at: new Date().toISOString() };
        setBranchMovements(prev => [mock, ...prev]);
        return { data: mock };
      }
      const userId = (await supabase.auth.getSession()).data?.session?.user?.id;
      const payload = { branch_id: branchId, movement_type: type, amount: amt, description: description || null, created_by: userId || null };
      const { data, error } = await supabase.from('branch_cash_movements').insert(payload).select('*').single();
      if (error) throw error;
      await refreshSelective([
        'branch_cash_movements',
        'v_branch_balances',
      ]);
      return { data };
    },
    // Internal branch transfers removed: method no longer available
    preferences,
    savePreferences: async (patch = {}) => {
      const merged = mergePreferences(patch);
      if (!useDb) return merged;
      const userId = (await supabase.auth.getSession()).data?.session?.user?.id;
      if (!userId) return merged;
      const payload = {
        user_id: userId,
        toast_position: merged.position || 'top-right',
        toast_max_items: Number(merged.maxItems ?? 4),
        toast_duration_ms: Number(merged.durationMs ?? 4000),
        theme: merged.theme || 'system',
        // Avoid optional columns that may not exist in older schemas
        // default_page and show_tips are best-effort; exclude when undefined
        ...(merged.defaultPage ? { default_page: merged.defaultPage } : {}),
        ...(typeof merged.showTips === 'boolean' ? { show_tips: merged.showTips } : {}),
      };
      // upsert into user_preferences
      let { error } = await supabase
        .from('user_preferences')
        .upsert(payload, { onConflict: 'user_id' });
      // Fallback: strip optional fields if schema doesn't have them
      if (error && (error.code === 'PGRST204' || error.code === '42703')) {
        const minimal = {
          user_id: payload.user_id,
          toast_position: payload.toast_position,
          toast_max_items: payload.toast_max_items,
          toast_duration_ms: payload.toast_duration_ms,
          theme: payload.theme,
        };
        ({ error } = await supabase
          .from('user_preferences')
          .upsert(minimal, { onConflict: 'user_id' }));
        if (error) throw error;
      } else if (error) {
        throw error;
      }
      return merged;
    },
    saveProfile: async (patch = {}) => {
      const desiredName = Object.prototype.hasOwnProperty.call(patch, 'name') ? String(patch.name || '') : undefined;
      const desiredUsername = Object.prototype.hasOwnProperty.call(patch, 'username') ? String(patch.username || '') : undefined;
      if (!useDb) {
        setCurrentUser((prev) => ({
          ...(prev || {}),
          ...(desiredName !== undefined ? { name: desiredName } : {}),
          ...(desiredUsername !== undefined ? { username: desiredUsername } : {}),
        }));
        return { ok: true };
      }
      const userId = (await supabase.auth.getSession()).data?.session?.user?.id;
      if (!userId) throw new Error('No hay sesión activa');
      const updates = {};
      if (desiredName !== undefined) updates.full_name = desiredName;
      if (desiredUsername !== undefined) {
        try { await supabase.auth.updateUser({ data: { username: desiredUsername } }); } catch {}
        // best-effort: also try to persist in profiles.username if exists
        try {
          await supabase.from('profiles').update({ username: desiredUsername }).eq('id', userId);
        } catch {}
      }
      if (Object.keys(updates).length > 0) {
        const { data, error } = await supabase
          .from('profiles')
          .update(updates)
          .eq('id', userId)
          .select('*')
          .single();
        if (error) throw error;
        setCurrentUser((prev) => ({ ...(prev || {}), name: data?.full_name || desiredName || (prev?.name || '') }));
      }
      if (desiredUsername !== undefined) {
        setCurrentUser((prev) => ({ ...(prev || {}), username: desiredUsername }));
      }
      return { ok: true, data };
    }
  };

  return (
    <DataContext.Provider value={value}>{children}</DataContext.Provider>
  );
}
